import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { DemoProvider, useDemo } from "@/contexts/DemoContext";
import { SettingsProvider, useSettings } from "@/contexts/SettingsContext";
import { canAccessRoute } from "@/lib/permissions";
import { Toaster } from "sonner";
import AppLayout from "@/components/layout/AppLayout";
import DemoTour from "@/components/DemoTour";
import Fehlergrenze from "@/components/Fehlergrenze";
// Musterseite der Bausteine (replica/design) — nur im Entwicklungsbuild; im Produktionsbuild faellt der Zweig weg
const DesignPage = process.env.NODE_ENV !== "production" ? lazy(() => import("@/pages/DesignPage")) : null;

// Public pages
import LandingPage from "@/pages/LandingPage";
import LoginPageNew from "@/pages/LoginPageNew";
import RegisterPage from "@/pages/RegisterPage";
import ImpressumPage from "@/pages/ImpressumPage";
import DatenschutzPage from "@/pages/DatenschutzPage";
import AppHilfePage from "@/pages/AppHilfePage";
import AppNutzungsbedingungenPage from "@/pages/AppNutzungsbedingungenPage";
import AGBPage from "@/pages/AGBPage";
import CookiesPage from "@/pages/CookiesPage";
import AuthCallback from "@/pages/AuthCallback";
import CaptureFormPage from "@/pages/CaptureFormPage";
import BeratungBuchungPage from "@/pages/BeratungBuchungPage";
import StammplatzWahlPage from "@/pages/StammplatzWahlPage";
import EmpfehlungPage from "@/pages/EmpfehlungPage";
import EventPage from "@/pages/EventPage";
import BeratungAbsagePage from "@/pages/BeratungAbsagePage";

// Protected pages (CRM)
import DashboardPage from "@/pages/DashboardPage";
import LeadsPage from "@/pages/LeadsPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import PipelinePage from "@/pages/PipelinePage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import FollowUpsPage from "@/pages/FollowUpsPage";
import SettingsPage from "@/pages/SettingsPage";
import TeamPage from "@/pages/TeamPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import AutomationsPage from "@/pages/AutomationsPage";
import ReportsPage from "@/pages/ReportsPage";
import CalendarPage from "@/pages/CalendarPage";
import TrainerChatPage from "@/pages/TrainerChatPage";
import MitgliedschaftenPage from "@/pages/MitgliedschaftenPage";
import KursplanPage from "@/pages/KursplanPage";
import HilfePage from "@/pages/HilfePage";
import SupportPage from "@/pages/SupportPage";
import UscPage from "@/pages/UscPage";
import GastpassKaufPage from "@/pages/GastpassKaufPage";
import GastpaessePage from "@/pages/GastpaessePage";
import CoachingPaketePage from "@/pages/CoachingPaketePage";
import CoachingBezahltPage from "@/pages/CoachingBezahltPage";
import CoachingAngebotPage from "@/pages/CoachingAngebotPage";
import KalorienrechnerPage from "@/pages/KalorienrechnerPage";
import AppApiPage from "@/pages/AppApiPage";
import EmpfangPage from "@/pages/EmpfangPage";
import PraemienPage from "@/pages/PraemienPage";
import ProbetrainingenPage from "@/pages/ProbetrainingenPage";
import VertriebPage from "@/pages/VertriebPage";
import CloserPage from "@/pages/CloserPage";
import { VorhabenListe, VorhabenDetail } from "@/pages/VorhabenPage";
import VertriebKennzahlenPage from "@/pages/VertriebKennzahlenPage";
import SalesTrackingPage from "@/pages/SalesTrackingPage";
import AnrufkampagnenPage from "@/pages/AnrufkampagnenPage";
import VorauszahlerPage from "@/pages/VorauszahlerPage";
import SetterPosteingangPage from "@/pages/SetterPosteingangPage";
import FreigabePage from "@/pages/FreigabePage";
import EinschaetzungenPage from "@/pages/EinschaetzungenPage";
import KurzlinksPage from "@/pages/KurzlinksPage";
import ReaktivierungPage from "@/pages/ReaktivierungPage";
import TeilnahmePage from "@/pages/TeilnahmePage";
import SeminarePage from "@/pages/SeminarePage";
import PruefungenPage from "@/pages/PruefungenPage";
import GymFightsTicketsPage from "@/pages/GymFightsTicketsPage";
import EventrechnerPage from "@/pages/EventrechnerPage";
import SchnellerfassungPage from "@/pages/SchnellerfassungPage";
import FunnelsPage from "@/pages/FunnelsPage";
import KursSeite from "@/pages/KursSeite";
import { FestFormular, FestKarte } from "@/pages/FestKartePage";
import FestScanPage from "@/pages/FestScanPage";
import FestScanEinfachPage from "@/pages/FestScanEinfachPage";
import FestDashboardPage from "@/pages/FestDashboardPage";
import MandantenPage from "@/pages/MandantenPage";
import ZinzinoLanding from "@/pages/ZinzinoLanding";
import FunnelSeite from "@/pages/FunnelSeite";
import ZinzinoMappePage from "@/pages/ZinzinoMappePage";
import ZinzinoCockpitPage from "@/pages/ZinzinoCockpitPage";
import PosteingangPage from "@/pages/PosteingangPage";
import AgentPage from "@/pages/AgentPage";
import GraduierungPage from "@/pages/GraduierungPage";
import VerkaufPage from "@/pages/VerkaufPage";
import VertraegePage from "@/pages/VertraegePage";
import ShopPage from "@/pages/ShopPage";

// Verkauf – interne Tarifreferenz
import VerkaufReferenzPage from "@/pages/VerkaufReferenzPage";
import SammelvertragPage from "@/pages/SammelvertragPage";
import VerkaufstrainerPage from "@/pages/VerkaufstrainerPage";
import ContentPlanPage from "@/pages/ContentPlanPage";
import TestimonialBibliothekPage from "@/pages/TestimonialBibliothekPage";
import CopilotPage from "@/pages/CopilotPage";
import PiaPage from "@/pages/PiaPage";
import PiaApp from "@/pages/PiaApp";
import HeutePage from "@/pages/HeutePage";
import KennzahlenPage from "@/pages/KennzahlenPage";
import CashflowPage from "@/pages/CashflowPage";
import TrainerHeutePage from "@/pages/TrainerHeutePage";
import AnwesenheitKioskPage from "@/pages/AnwesenheitKioskPage";
import CheckupTabletPage from "@/pages/CheckupTabletPage";
import PlanerPage from "@/pages/PlanerPage";
import ErnaehrungPage from "@/pages/ErnaehrungPage";
import WaagePage from "@/pages/WaagePage";
import ErnaehrungMitgliedPage from "@/pages/ErnaehrungMitgliedPage";
import ErnaehrungVorlagePage from "@/pages/ErnaehrungVorlagePage";
import ErnaehrungAbschlussPage from "@/pages/ErnaehrungAbschlussPage";
import ErnaehrungBezahltPage from "@/pages/ErnaehrungBezahltPage";
import CoachingCockpitPage from "@/pages/CoachingCockpitPage";
import UnterschriftStationPage from "@/pages/UnterschriftStationPage";
import UnterschriftQrPage from "@/pages/UnterschriftQrPage";
import CoachPage from "@/pages/CoachPage";
import { CoachKundeUmleitung } from "@/pages/CoachKundeAkte";
import AutomatikenPage from "@/pages/AutomatikenPage";
import RundschreibenPage from "@/pages/RundschreibenPage";
import ArbeitsberichtPage from "@/pages/ArbeitsberichtPage";
import KursbestandPage from "@/pages/KursbestandPage";
import LehrplanPage from "@/pages/LehrplanPage";
import EinsatzTrainerPage from "@/pages/EinsatzTrainerPage";
import MandatePage from "@/pages/MandatePage";
import RechnungslaufPage from "@/pages/RechnungslaufPage";
import FinanzenRechnungenPage from "@/pages/FinanzenRechnungenPage";
import TrainerKalenderPage from "@/pages/TrainerKalenderPage";

// /kunden/:id → /leads/:id (Kundenakte, 28.10.2026). Query (z. B. ?tab=) geht mit.
function KundeUmleitung() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/leads/${id}${location.search}`} replace />;
}

function ProtectedRoute({ children }) {
  const { user, loading, userPermissions, isAdminPermissions } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <img src="/logo-black.png" alt="Performance Center" className="h-10 mx-auto object-contain opacity-60" />
          <div className="w-6 h-6 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground text-xs">Wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Unterschrift-Station (03.10.2026): dieses Konto sieht nur seine Stationsseite — der Server
  // sperrt es zusaetzlich auf seine Pfade ein (stationen.py)
  if (user.station === 'unterschrift') {
    return <Navigate to="/unterschrift" replace />;
  }

  if (!canAccessRoute(user.role, location.pathname, userPermissions, isAdminPermissions)) {
    return <Navigate to="/verkauf" replace />;
  }

  // Fehlergrenze INNERHALB des Layouts: stuerzt eine Seite ab, bleibt die
  // Seitenleiste bedienbar und die Meldung steht dort, wo der Inhalt waere.
  return <AppLayout><Fehlergrenze pfad={location.pathname}>{children}</Fehlergrenze></AppLayout>;
}

function ProtectedMinimalRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.station === 'unterschrift') return <Navigate to="/unterschrift" replace />;
  return children;
}

function DemoOverlay() {
  const { demoActive, stopDemo } = useDemo();
  return <DemoTour active={demoActive} onClose={stopDemo} />;
}

function SettingsRefresher() {
  const location = useLocation();
  const { onNavigate } = useSettings();
  useEffect(() => { onNavigate(); }, [location.pathname, onNavigate]);
  return null;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Stations-Rollen landen auf ihrer Seite (Unterschrift-Station, Trainer-Tablet), alle anderen auf /heute
  if (user) return <Navigate to={user.startseite || "/heute"} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <SettingsRefresher />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<PublicOnlyRoute><LandingPage /></PublicOnlyRoute>} />
        <Route path="/login" element={<PublicOnlyRoute><LoginPageNew /></PublicOnlyRoute>} />
        <Route path="/registrieren" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/hilfe" element={<HilfePage />} />
        {/* Support-Adresse für den App Store — muss ohne Anmeldung erreichbar sein */}
        <Route path="/app-hilfe" element={<AppHilfePage />} />
        <Route path="/app-nutzungsbedingungen" element={<AppNutzungsbedingungenPage />} />
        <Route path="/agb" element={<AGBPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/capture" element={<CaptureFormPage />} />
        {/* Öffentliche Beratungsbuchung (QR-Ziel) — bewusst ohne Auth-Wrapper */}
        <Route path="/buchung/beratung" element={<BeratungBuchungPage />} />
        <Route path="/buchung/stammplatz/:code" element={<StammplatzWahlPage />} />
        <Route path="/empfehlung/:code" element={<EmpfehlungPage />} />
        <Route path="/event/:eventId/:code" element={<EventPage />} />
        <Route path="/buchung/beratung/absage/:token" element={<BeratungAbsagePage />} />
        {/* Öffentliche Mandanten-Landingpages — KEIN Auth-Guard, verlinkbar
            über Flyer/QR/WhatsApp. /zinzino-termin springt direkt zum
            Slot-Picker (Deep-Link für Warmkontakte). */}
        {/* Probetrainings-Funnel (Bauauftrag-Paket p1-funnels) — öffentlich */}
        <Route path="/funnelkids" element={<FunnelSeite slug="kids" />} />
        <Route path="/funnel" element={<FunnelSeite slug="kampfsport" />} />
        <Route path="/frauen" element={<FunnelSeite slug="frauen" />} />
        {/* Alt-Slugs aus OneClickGym: gedruckte QR-Codes zeigen noch dorthin */}
        <Route path="/kids-funnel" element={<Navigate to="/funnelkids" replace />} />
        <Route path="/kampfsportfunnel" element={<Navigate to="/funnel" replace />} />
        <Route path="/kurs/:slug" element={<KursSeite />} />
        {/* Unterschrift-Station (03.10.2026): /unterschrift = Theken-iPad (eigene Anmeldepruefung, ohne Menue),
            /u/<token> = QR-Seite des Kunden ohne Anmeldung */}
        <Route path="/unterschrift" element={<UnterschriftStationPage />} />
        <Route path="/u/:token" element={<UnterschriftQrPage />} />
        {/* Gastpass (11.10.2026): oeffentliche Kaufseite fuer S&C/HIIT ohne Mitgliedschaft */}
        <Route path="/gastpass" element={<GastpassKaufPage />} />
        {/* P1 Coaching (12.10.2026): Danke-Seite nach der Stripe-Zahlung, ohne Anmeldung */}
        <Route path="/coaching-bezahlt" element={<CoachingBezahltPage />} />
        {/* E23 (15.10.2026): Danke-Seite nach dem Stripe-Abo fürs Ernährungscoaching, ohne Anmeldung */}
        <Route path="/ernaehrung-bezahlt" element={<ErnaehrungBezahltPage />} />
        {/* Angebotsseite als Leadseite (Ziel der Social-Anzeigen), ohne Anmeldung */}
        <Route path="/p1-coaching" element={<CoachingAngebotPage />} />
        {/* E14 (15.10.2026): Kalorienrechner als Lead-Magnet, ohne Anmeldung */}
        <Route path="/lp/kalorienrechner" element={<KalorienrechnerPage />} />
        {/* Trainer-Webseite (10.10.2026): eigene Anmeldung per E-Mail-Code, kein CRM-Konto */}
        <Route path="/coach" element={<CoachPage />} />
        {/* Kundenakte auf /coach (W1.4, 29.10.2026): öffnet CoachPage mit ?ansicht=kunde&kunde=<id> */}
        <Route path="/coach/kunden/:id" element={<CoachKundeUmleitung />} />
        {/* W2.5 (29.10.2026): /coach/checkins u. a. — derselbe Einstieg, der Bereich kommt aus dem Pfad */}
        <Route path="/coach/:bereich" element={<CoachPage />} />
        {/* Coach-Cockpit W2 (29.10.2026): Startseite, Portfolio, Nachrichten als eigene Adressen derselben Seite */}
        <Route path="/coach/heute" element={<CoachPage start="heute" />} />
        <Route path="/coach/fortschritt" element={<CoachPage start="fortschritt" />} />
        <Route path="/coach/nachrichten" element={<CoachPage start="nachrichten" />} />
        {/* Trainer-Kalender per Link, ohne Login (18.09.2026) */}
        <Route path="/trainer/heute" element={<ProtectedRoute><TrainerHeutePage /></ProtectedRoute>} />
        <Route path="/trainer/:token" element={<TrainerKalenderPage />} />
        {/* Anwesenheits-Tafel fuers Saeulen-iPad (06.10.2026): Kacheln, ohne Login, Token = Trainer-Link */}
        <Route path="/tafel/:token" element={<AnwesenheitKioskPage />} />
        <Route path="/checkup/:token" element={<CheckupTabletPage />} />
        {/* Alt-Slugs der GEDRUCKTEN QR-Codes im Studio: die Weiterleitung
            markiert sich selbst (utm_source=qr -> Kanal "aushang"), damit
            die bereits haengenden Codes trackbar sind — ohne Neudruck. */}
        <Route path="/probetrainings-buchung-gym-qr" element={<Navigate to="/funnel?utm_source=qr&utm_campaign=gym-aushang" replace />} />
        <Route path="/probetrainings-buchung-widget" element={<Navigate to="/funnel?utm_source=qr&utm_campaign=widget" replace />} />
        <Route path="/zinzino" element={<ZinzinoLanding />} />
        <Route path="/zinzino-termin" element={<ZinzinoLanding />} />
        {/* Sommerfest-Festkarte: Gastseiten öffentlich (QR-Ziel am Eingang),
            Scanner + Dashboard hinter Login weiter unten */}
        <Route path="/fest" element={<FestFormular />} />
        <Route path="/fest/k/:code" element={<FestKarte />} />
        {/* /ptl ist eine statische Seite (public/ptl.html) — Caddy liefert
            sie vor dem SPA-Fallback aus, hier braucht es keine Route */}

        {/* Protected CRM routes */}
        <Route path="/verkauf/referenz" element={<ProtectedRoute><VerkaufReferenzPage /></ProtectedRoute>} />
        <Route path="/verkauf/geschwister" element={<ProtectedRoute><SammelvertragPage /></ProtectedRoute>} />
        {/* v6 (M8): /heute ist die Landung nach dem Login, das Dashboard bleibt erreichbar */}
        <Route path="/heute" element={<ProtectedRoute><HeutePage /></ProtectedRoute>} />
        <Route path="/kennzahlen" element={<ProtectedRoute><KennzahlenPage /></ProtectedRoute>} />
        <Route path="/cashflow" element={<ProtectedRoute><CashflowPage /></ProtectedRoute>} />
        <Route path="/automatiken" element={<ProtectedRoute><AutomatikenPage /></ProtectedRoute>} />
        <Route path="/rundschreiben" element={<ProtectedRoute><RundschreibenPage /></ProtectedRoute>} />
        <Route path="/trainer" element={<ProtectedRoute><TrainerHeutePage /></ProtectedRoute>} />
        <Route path="/arbeitsbericht" element={<ProtectedRoute><ArbeitsberichtPage /></ProtectedRoute>} />
        <Route path="/kursbestand" element={<ProtectedRoute><KursbestandPage /></ProtectedRoute>} />
        {/* Trainingsplaner A5: Leseuebersicht aller laufenden Zyklen + Druckansicht */}
        <Route path="/planer" element={<ProtectedRoute><PlanerPage /></ProtectedRoute>} />
        {/* Lehrplan (15.10.2026): Inhalte je Gürtel — Recht über NAV_ITEMS (kursplan.view) */}
        <Route path="/lehrplan" element={<ProtectedRoute><LehrplanPage /></ProtectedRoute>} />
        {/* Einsatzplanung Schritt 1 (16.10.2026): Trainer-Profile — lesen Leads/Kurspflege, schreiben Leitung (Server) */}
        <Route path="/einsatzplan/trainer" element={<ProtectedRoute><EinsatzTrainerPage /></ProtectedRoute>} />
        <Route path="/ernaehrung" element={<ProtectedRoute><ErnaehrungPage /></ProtectedRoute>} />
        <Route path="/waage" element={<ProtectedRoute><WaagePage /></ProtectedRoute>} />
        {/* E23 (15.10.2026): „Neuer Kunde in einem Fluss“ — vor /ernaehrung/:leadId, sonst fängt die Mitglieds-Seite */}
        <Route path="/ernaehrung/abschluss" element={<ProtectedRoute><ErnaehrungAbschlussPage /></ProtectedRoute>} />
        <Route path="/ernaehrung/:leadId" element={<ProtectedRoute><ErnaehrungMitgliedPage /></ProtectedRoute>} />
        <Route path="/ernaehrung/vorlagen/:vid" element={<ProtectedRoute><ErnaehrungVorlagePage /></ProtectedRoute>} />
        {/* E6 Coach-Cockpit (15.10.2026): drei Spalten Kundenliste · Konversation · Kundenakte */}
        <Route path="/coaching-cockpit" element={<ProtectedRoute><CoachingCockpitPage /></ProtectedRoute>} />
        <Route path="/mandate" element={<ProtectedRoute><MandatePage /></ProtectedRoute>} />
        <Route path="/rechnungen" element={<ProtectedRoute><RechnungslaufPage /></ProtectedRoute>} />
        <Route path="/finanzen/rechnungen" element={<ProtectedRoute><FinanzenRechnungenPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
        <Route path="/leads/:id" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
        {/* Kundenakte (28.10.2026): /kunden/:id ist /leads/:id — keine zweite Akte */}
        <Route path="/kunden/:id" element={<ProtectedRoute><KundeUmleitung /></ProtectedRoute>} />
        <Route path="/pipeline" element={<ProtectedRoute><PipelinePage /></ProtectedRoute>} />
        <Route path="/activities" element={<ProtectedRoute><ActivitiesPage /></ProtectedRoute>} />
        <Route path="/follow-ups" element={<ProtectedRoute><FollowUpsPage /></ProtectedRoute>} />
        <Route path="/zinzino-mappe" element={<ProtectedRoute><ZinzinoMappePage /></ProtectedRoute>} />
        <Route path="/zinzino-cockpit" element={<ProtectedRoute><ZinzinoCockpitPage /></ProtectedRoute>} />
        <Route path="/reporting" element={<Navigate to="/reports?tab=kampagnen" replace />} />
        <Route path="/posteingang" element={<ProtectedRoute><PosteingangPage /></ProtectedRoute>} />
        <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
        <Route path="/usc" element={<ProtectedRoute><UscPage /></ProtectedRoute>} />
        <Route path="/gastpaesse" element={<ProtectedRoute><GastpaessePage /></ProtectedRoute>} />
        <Route path="/coaching" element={<ProtectedRoute><CoachingPaketePage /></ProtectedRoute>} />
        <Route path="/agent" element={<ProtectedRoute><AgentPage /></ProtectedRoute>} />
        <Route path="/kalender" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
        <Route path="/trainer-chat" element={<ProtectedRoute><TrainerChatPage /></ProtectedRoute>} />
        <Route path="/checkin" element={<Navigate to="/empfang" replace />} />
        <Route path="/mitgliedschaften" element={<ProtectedRoute><MitgliedschaftenPage /></ProtectedRoute>} />
        <Route path="/kursplan" element={<ProtectedRoute><KursplanPage /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
        <Route path="/app-api" element={<ProtectedRoute><AppApiPage /></ProtectedRoute>} />
        <Route path="/verkauf" element={<ProtectedRoute><VerkaufPage /></ProtectedRoute>} />
        <Route path="/calendar" element={<Navigate to="/kalender" replace />} />
        <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
        <Route path="/empfang" element={<ProtectedRoute><EmpfangPage /></ProtectedRoute>} />
        <Route path="/praemien" element={<ProtectedRoute><PraemienPage /></ProtectedRoute>} />
        <Route path="/vertrieb" element={<ProtectedRoute><VertriebPage /></ProtectedRoute>} />
        <Route path="/closer" element={<ProtectedRoute><CloserPage /></ProtectedRoute>} />
        <Route path="/verkaufstrainer" element={<ProtectedRoute><VerkaufstrainerPage /></ProtectedRoute>} />
        <Route path="/content" element={<ProtectedRoute><ContentPlanPage /></ProtectedRoute>} />
        <Route path="/testimonials" element={<ProtectedRoute><TestimonialBibliothekPage /></ProtectedRoute>} />
        <Route path="/copilot" element={<ProtectedRoute><CopilotPage /></ProtectedRoute>} />
        <Route path="/pia" element={<ProtectedRoute><PiaPage /></ProtectedRoute>} />
        <Route path="/pia/app" element={<ProtectedMinimalRoute><PiaApp /></ProtectedMinimalRoute>} />
        {/* Brief 29.08. nennt die Seite /vertrieb — beide Wege fuehren hierher */}
        <Route path="/vertrieb" element={<ProtectedRoute><CloserPage /></ProtectedRoute>} />
        <Route path="/vertriebszahlen" element={<ProtectedRoute><VertriebKennzahlenPage /></ProtectedRoute>} />
        <Route path="/kennzahlen/vertrieb" element={<ProtectedRoute><SalesTrackingPage /></ProtectedRoute>} />
        <Route path="/kampagnen/anrufe" element={<ProtectedRoute><AnrufkampagnenPage /></ProtectedRoute>} />
        <Route path="/kampagnen/anrufe/:id" element={<ProtectedRoute><AnrufkampagnenPage /></ProtectedRoute>} />
        <Route path="/vorauszahler" element={<ProtectedRoute><VorauszahlerPage /></ProtectedRoute>} />
        <Route path="/setter" element={<ProtectedRoute><SetterPosteingangPage /></ProtectedRoute>} />
        <Route path="/freigabe" element={<ProtectedRoute><FreigabePage /></ProtectedRoute>} />
        <Route path="/einschaetzungen" element={<ProtectedRoute><EinschaetzungenPage /></ProtectedRoute>} />
        <Route path="/kurzlinks" element={<ProtectedRoute><KurzlinksPage /></ProtectedRoute>} />
        <Route path="/vorhaben" element={<ProtectedRoute><VorhabenListe /></ProtectedRoute>} />
        <Route path="/vorhaben/:id" element={<ProtectedRoute><VorhabenDetail /></ProtectedRoute>} />
        <Route path="/probetrainings" element={<ProtectedRoute><ProbetrainingenPage /></ProtectedRoute>} />
        <Route path="/reaktivierung" element={<ProtectedRoute><ReaktivierungPage /></ProtectedRoute>} />
        <Route path="/teilnahme" element={<ProtectedRoute><TeilnahmePage /></ProtectedRoute>} />
        <Route path="/funnels" element={<ProtectedRoute><FunnelsPage /></ProtectedRoute>} />
        {/* Fest: Scanner ohne CRM-Layout (Vollbild, Handy am Stand),
            Berechtigung fest.* prüft das Backend */}
        {/* Team-Scan ohne Login (Brief 11.09.2026): ein Link je Trainer,
            ?wer=&station=; der volle Scanner mit Kurswahl liegt hinter Login.
            /erfassung ist der kurze Name, den das Team auf den Zetteln hat. */}
        <Route path="/fest/scan" element={<FestScanEinfachPage />} />
        <Route path="/fest/scan-voll" element={<ProtectedMinimalRoute><FestScanPage /></ProtectedMinimalRoute>} />
        <Route path="/erfassung" element={<Navigate to="/fest/scan" replace />} />
        <Route path="/fest/dashboard" element={<ProtectedMinimalRoute><FestDashboardPage /></ProtectedMinimalRoute>} />
        <Route path="/seminare" element={<ProtectedRoute><SeminarePage /></ProtectedRoute>} />
        <Route path="/pruefungen" element={<ProtectedRoute><PruefungenPage /></ProtectedRoute>} />
        <Route path="/gym-fights-tickets" element={<ProtectedRoute><GymFightsTicketsPage /></ProtectedRoute>} />
        <Route path="/eventrechner" element={<ProtectedRoute><EventrechnerPage /></ProtectedRoute>} />
        <Route path="/schnell" element={<ProtectedRoute><SchnellerfassungPage /></ProtectedRoute>} />
        <Route path="/mandanten/:mandant" element={<ProtectedRoute><MandantenPage /></ProtectedRoute>} />
        <Route path="/mandanten" element={<Navigate to="/mandanten/zinzino" replace />} />
        <Route path="/zinzino/termine" element={<Navigate to="/mandanten/zinzino" replace />} />
        <Route path="/graduierung" element={<ProtectedRoute><GraduierungPage /></ProtectedRoute>} />
        <Route path="/automations" element={<ProtectedRoute><AutomationsPage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
        <Route path="/vertraege" element={<ProtectedRoute><VertraegePage /></ProtectedRoute>} />
        {DesignPage && <Route path="/design" element={<Suspense fallback={null}><DesignPage /></Suspense>} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <DemoOverlay />
    </>
  );
}

function App() {
  useEffect(() => {
    const hide = () => {
      document.querySelectorAll('body > div:not(#root)').forEach(el => {
        if (el.textContent?.includes('Made with Emergent') || el.textContent?.includes('Made with  Emergent')) {
          el.style.cssText = 'display:none!important;visibility:hidden!important;height:0!important;overflow:hidden!important';
        }
      });
    };
    hide();
    const observer = new MutationObserver(hide);
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <SettingsProvider>
          <DemoProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
          </DemoProvider>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
